import { normalizeGameType } from '../config/gameTypes';

const DEFAULT_COLORS = [
  'from-blue-500 to-indigo-500',
  'from-emerald-500 to-teal-500',
  'from-amber-500 to-orange-500',
  'from-rose-500 to-pink-500',
  'from-purple-500 to-fuchsia-500',
  'from-cyan-500 to-sky-500'
];

const getSiteGameType = () => {
  const siteType =
    (typeof window !== 'undefined' && window.SITE_CONFIG?.defaults?.game_type) ||
    (typeof window !== 'undefined' && window.SITE_CONFIG?.game_type?.id);
  return normalizeGameType(siteType) || null;
};

const normalizeAmount = (value) => {
  if (value === null || value === undefined) return null;
  if (typeof value === 'number') return value;
  if (typeof value === 'string') {
    const cleaned = value.replace(/[^0-9.-]/g, '');
    const parsed = Number(cleaned);
    return Number.isNaN(parsed) ? null : parsed;
  }
  return null;
};

export const resolvePrizeDescription = (prizeDescriptions, language) => {
  if (!prizeDescriptions) return null;
  if (typeof prizeDescriptions === 'string') return prizeDescriptions.trim() || null;
  if (typeof prizeDescriptions !== 'object') return null;
  return (
    prizeDescriptions[language] ||
    prizeDescriptions.en ||
    prizeDescriptions['en-CA'] ||
    Object.values(prizeDescriptions)[0] ||
    null
  );
};

const formatDate = (value, language = 'en') => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  const locale = language === 'fr' ? 'fr-CA' : language === 'es' ? 'es-MX' : 'en-CA';
  return date.toLocaleDateString(locale, {
    month: 'long',
    day: 'numeric',
    year: 'numeric'
  });
};

// Translates "Draw", "Session", "Week", "Game" prefixes based on language
const DRAW_LABEL_TRANSLATIONS = {
  en: { draw: 'Draw', session: 'Session', week: 'Week', game: 'Game' },
  fr: { draw: 'Tirage', session: 'Session', week: 'Semaine', game: 'Jeu' },
  es: { draw: 'Sorteo', session: 'Sesión', week: 'Semana', game: 'Juego' },
};

const resolveDrawLabel = (winner, gameType, language = 'en') => {
  const lbl = DRAW_LABEL_TRANSLATIONS[language] || DRAW_LABEL_TRANSLATIONS.en;
  const draw = winner.draw || winner.Draw;
  if (draw) return draw;

  // Prefer a real draw number before falling back to game/week IDs or GUIDs.
  const drawNumber = winner.drawNumber ?? winner.DrawNumber ?? winner.draw_number;
  if (drawNumber != null) return `${lbl.draw} #${drawNumber}`;

  // For Superball Bingo, use session number
  const sessionNumber = winner.sessionNumber ?? winner.SessionNumber;
  if (sessionNumber || gameType === 'superballBingo') {
    return `${lbl.session} ${sessionNumber || '?'}`;
  }

  const weekNumber = winner.weekNumber ?? winner.WeekNumber;
  const gameNumber = winner.gameNumber ?? winner.GameNumber;

  // For CTA (and any game with both values), include game number to prevent
  // duplicate "Week X" labels across different game runs.
  if (weekNumber && gameNumber) return `G${gameNumber} W${weekNumber}`;
  if (weekNumber) return `${lbl.week} ${weekNumber}`;

  if (gameNumber) return `${lbl.game} ${gameNumber}`;

  // Don't render a raw GUID as the label; fall back to the generic label
  // when no human-readable identifier is available. Draw numbers for past
  // winners must come from the API (see winners endpoint enhancement).
  return lbl.draw;
};

const resolveCardDrawn = (winner) => {
  const cardNumber = winner.cardNumber || winner.CardNumber;
  const cardSuit = winner.cardSuit || winner.CardSuit;
  if (!cardNumber && !cardSuit) return null;

  // Map suit names to glyphs
  const suitRaw = String(cardSuit || '').toLowerCase().trim();
  const suitMap = {
    heart: '♥', hearts: '♥',
    diamond: '♦', diamonds: '♦',
    club: '♣', clubs: '♣',
    spade: '♠', spades: '♠',
  };
  const suitGlyph = suitMap[suitRaw] || cardSuit || '';
  const rankMap = { jack: 'J', queen: 'Q', king: 'K', ace: 'A' };
  const rankRaw = String(cardNumber || '').trim();
  const rank = rankMap[rankRaw.toLowerCase()] || rankRaw;

  return `${rank}${suitGlyph}`;
};

const resolveName = (winner) => {
  return (
    winner.name ||
    winner.Name ||
    winner.displayName ||
    winner.DisplayName ||
    winner.ticketNumber ||
    winner.TicketNumber ||
    'Winner'
  );
};

export const mapWinnersFromApi = (winners, options = {}) => {
  const { language = 'en', gameType = getSiteGameType() } = options;
  if (!Array.isArray(winners)) return [];

  return winners.map((winner, index) => {
    const statusRaw = winner.status || winner.Status || 'winner';
    const status = String(statusRaw).toLowerCase();
    const isWinner = status !== 'non-winner' && status !== 'nonwinner';
    const amount = normalizeAmount(winner.amount ?? winner.Amount) ?? 0;
    const name = resolveName(winner);
    const resolvedGameType = gameType || winner.gameType || winner.GameType || null;
    const prizeDescription = resolvePrizeDescription(
      winner.prizeDescriptions || winner.PrizeDescriptions || winner.prizeDescription || winner.PrizeDescription,
      language
    );
    const prizeName = winner.prizeName || winner.PrizeName || null;
    const isPrizeFlag = winner.isPrize ?? winner.IsPrize ?? false;
    // Surface prize description whenever it is provided (e.g. prize draws).
    const prize = prizeName || prizeDescription || null;
    const ticketNumber = winner.ticketNumber || winner.TicketNumber || '';
    const drawDate = winner.date || winner.Date;

    return {
      id: winner.id || winner.Id || winner.drawId || winner.DrawId || `${index}-${ticketNumber}`,
      name,
      amount,
      prize,
      status,
      isWinner,
      isPrize: (winner.isPrize ?? winner.IsPrize ?? false) || (resolvedGameType === 'prizeDraw' && !!prize) || !!(winner.additionalDrawId || winner.AdditionalDrawId),
      additionalDrawId: winner.additionalDrawId || winner.AdditionalDrawId || null,
      prizeName: prizeName || winner.prizeName || winner.PrizeName || null,
      prizeDescription: prizeDescription || winner.prizeDescription || winner.PrizeDescription || null,
      picture: winner.picture || winner.Picture || null,
      drawId: winner.drawId || winner.DrawId || null,
      squareNumber: winner.squareNumber || winner.SquareNumber || null,
      date: formatDate(drawDate, language),
      draw: resolveDrawLabel(winner, resolvedGameType, language),
      avatar: name.trim().charAt(0).toUpperCase() || 'W',
      color: DEFAULT_COLORS[index % DEFAULT_COLORS.length],
      reason: isWinner ? '' : 'Ticket drawn was not eligible',
      ticketNumber,
      year: drawDate ? new Date(drawDate).getFullYear() : null,
      month: drawDate ? new Date(drawDate).getMonth() + 1 : null,
      gameType: resolvedGameType,
      drawNumber: winner.drawNumber ?? winner.DrawNumber ?? winner.draw_number ?? null,
      gameNumber: winner.gameNumber ?? winner.GameNumber ?? null,
      weekNumber: winner.weekNumber ?? winner.WeekNumber ?? null,
      isSecondaryPrize: winner.isSecondaryPrize ?? winner.IsSecondaryPrize ?? false,
      secondaryPrize: winner.isSecondaryPrize || winner.IsSecondaryPrize ? amount : null,
      cardDrawn: resolveCardDrawn(winner),
      cardSuit: winner.cardSuit || winner.CardSuit || null,
      cardNumber: winner.cardNumber || winner.CardNumber || null,
      totalTicketsSold:
        winner.totalTicketsSold ??
        winner.TotalTicketsSold ??
        winner.totalTickets ??
        winner.TotalTickets ??
        null,
      // Superball Bingo specific fields
      ballSelected: winner.ballSelected ?? winner.BallSelected ?? null,
      isJackpot: winner.isJackpot ?? winner.IsJackpot ?? false,
      sessionNumber: winner.sessionNumber ?? winner.SessionNumber ?? null
    };
  });
};

export const computeWinnerStats = (winners) => {
  if (!Array.isArray(winners) || winners.length === 0) {
    return {
      totalWinners: 0,
      totalPaidOut: 0,
      averageWin: 0,
      biggestWin: 0
    };
  }

  // A row counts as a paid winner if it won the main/jackpot prize
  // (isWinner !== false) OR a secondary/weekly prize. CTA weekly winners come
  // back as status "non-winner" (jackpot not found) but still won the weekly
  // pot, so they must be included in the stats.
  const winnersOnly = winners.filter(
    w => w.isWinner !== false || w.isSecondaryPrize === true || !!w.secondaryPrize
  );
  const totalWinners = winnersOnly.length;
  const totalPaidOut = winnersOnly.reduce((sum, w) => sum + (w.amount || 0), 0);
  const biggestWin = winnersOnly.reduce((max, w) => Math.max(max, w.amount || 0), 0);
  const averageWin = totalWinners ? Math.round(totalPaidOut / totalWinners) : 0;

  return {
    totalWinners,
    totalPaidOut,
    averageWin,
    biggestWin
  };
};
